import React, { useState, useEffect } from "react";
import "./NotificationRuleDialog.css";
import { Modal, Input, Row, Col, Select, InputNumber, Checkbox, Switch, message } from "antd";
import { useSelector } from "react-redux";
import lang from "../../../../services/lang";
import { GetMachineList } from "../../../../services/common/cnc_apis";
import Urls, { postRequest } from "../../../../services/common/urls";

const { Option } = Select;
const { TextArea } = Input;

function NotificationRuleDialog(props) {
  const { langIndex } = useSelector((x) => x.app);
  const {
    initRuleInfo,
    isShowModal,
    setIsShowModal,
    addRule,
    editRule,
    selCustomerId,
  } = props;
  const [ruleInfo, setRuleInfo] = useState({
    rule_name: "",
    trigger_type: "cycle_stop_alert",
    trigger_config: {},
    notification_methods: [],
    recipients: { email: [], phone: [], app: [] },
    timing_type: "immediate",
    delay_minutes: 0,
    repeat_type: "one_time",
    repeat_interval_minutes: null,
    is_active: 1,
  });
  const [machineList, setMachineList] = useState([]);
  const [downtimeReasons, setDowntimeReasons] = useState([]); // Combined downtime reasons from selected machines
  const [emailRecipientsText, setEmailRecipientsText] = useState(""); // Raw text for email recipients
  const [phoneRecipientsText, setPhoneRecipientsText] = useState(""); // Raw text for phone recipients

  useEffect(() => {
    if (initRuleInfo != null) {
      // Ensure JSON fields are properly parsed
      let triggerConfig = initRuleInfo.trigger_config || {};
      let triggerType = initRuleInfo.trigger_type;
      
      // Normalize trigger_config structure based on trigger_type
      if (triggerType === "cycle_stop_alert") {
        triggerConfig = {
          machine_ids: triggerConfig.machine_ids || [],
          time_threshold_minutes: triggerConfig.time_threshold_minutes || 30,
        };
      } else if (triggerType === "machine_status") {
        triggerConfig = {
          machine_ids: triggerConfig.machine_ids || [],
          status_type: triggerConfig.status_type || "offline",
          time_threshold_minutes: triggerConfig.time_threshold_minutes || 20,
          downtime_reasons: triggerConfig.downtime_reasons || [],
        };
      }
      
      // Normalize is_active: API/DB may return 1/"1"/true or 0/"0"/false
      const isActive = initRuleInfo.is_active === 1 || initRuleInfo.is_active === "1" || initRuleInfo.is_active === true;
      const parsedRule = {
        ...initRuleInfo,
        trigger_type: triggerType,
        trigger_config: triggerConfig,
        notification_methods: initRuleInfo.notification_methods || [],
        recipients: initRuleInfo.recipients || { email: [], phone: [], app: [] },
        is_active: isActive ? 1 : 0,
      };
      setRuleInfo(parsedRule);
      // Initialize text fields from recipients arrays
      setEmailRecipientsText((parsedRule.recipients.email || []).join('; '));
      setPhoneRecipientsText((parsedRule.recipients.phone || []).join('; '));
    } else {
      // New rule - initialize with default values
      setRuleInfo({
        rule_name: "",
        trigger_type: "cycle_stop_alert",
        trigger_config: { machine_ids: [], time_threshold_minutes: 30 },
        notification_methods: [],
        recipients: { email: [], phone: [], app: [] },
        timing_type: "immediate",
        delay_minutes: 0,
        repeat_type: "one_time",
        repeat_interval_minutes: null,
        is_active: 1,
      });
      // Initialize text fields as empty
      setEmailRecipientsText("");
      setPhoneRecipientsText("");
    }
  }, [initRuleInfo]);

  useEffect(() => {
    // Load machines
    GetMachineList(selCustomerId, (mList) => {
      if (mList != null && mList.length > 0) {
        setMachineList(mList);
      } else {
        setMachineList([]);
      }
    });
  }, [selCustomerId]);

  // Load downtime reasons when machines are selected for machine_status trigger with downtime_reason status_type
  useEffect(() => {
    if (ruleInfo.trigger_type === "machine_status" && 
        ruleInfo.trigger_config?.status_type === "downtime_reason" &&
        ruleInfo.trigger_config && 
        ruleInfo.trigger_config.machine_ids && 
        ruleInfo.trigger_config.machine_ids.length > 0) {
      loadDowntimeReasons(ruleInfo.trigger_config.machine_ids);
    } else {
      setDowntimeReasons([]);
    }
  }, [ruleInfo.trigger_type, ruleInfo.trigger_config?.status_type, ruleInfo.trigger_config?.machine_ids]);

  const loadDowntimeReasons = (machineIds) => {
    // Load app settings for each selected machine and extract downtime reasons
    const allReasons = new Set();
    let loadedCount = 0;

    machineIds.forEach((machineId) => {
      const url = Urls.GET_APP_SETTING;
      const param = {
        factory_id: selCustomerId,
        machine_id: machineId,
      };

      postRequest(url, param, (res) => {
        loadedCount++;
        if (res.status === true && res.data) {
          // Extract downtime_reason1 through downtime_reason8
          for (let i = 1; i <= 8; i++) {
            const reason = res.data[`downtime_reason${i}`];
            if (reason && reason.trim() !== "") {
              allReasons.add(reason.trim());
            }
          }
        }

        // When all machines are loaded, update state
        if (loadedCount === machineIds.length) {
          setDowntimeReasons(Array.from(allReasons).sort());
        }
      });
    });
  };

  // Validation functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidPhone = (phone) => {
    // Allow various phone formats: +1234567890, (123) 456-7890, 123-456-7890, 1234567890, etc.
    // Remove common separators and check if it's a valid phone number
    const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
    // Check if it starts with + and has 10-15 digits, or has 10-15 digits without +
    const phoneRegex = /^(\+?[1-9]\d{9,14}|\d{10,15})$/;
    return phoneRegex.test(cleaned);
  };

  const onClickOk = () => {
    if (!ruleInfo.rule_name || ruleInfo.rule_name.trim() === "") {
      message.error(lang(langIndex, "notification_rule_name_required"));
      return;
    }

    // Validate trigger config based on trigger type
    if (ruleInfo.trigger_type === "cycle_stop_alert") {
      if (!ruleInfo.trigger_config.machine_ids || ruleInfo.trigger_config.machine_ids.length === 0) {
        message.error(lang(langIndex, "notification_machines_required"));
        return;
      }
      if (!ruleInfo.trigger_config.time_threshold_minutes || ruleInfo.trigger_config.time_threshold_minutes <= 0) {
        message.error(lang(langIndex, "notification_downtime_minutes_required"));
        return;
      }
    } else if (ruleInfo.trigger_type === "machine_status") {
      if (!ruleInfo.trigger_config.machine_ids || ruleInfo.trigger_config.machine_ids.length === 0) {
        message.error(lang(langIndex, "notification_machines_required"));
        return;
      }
      if (!ruleInfo.trigger_config.status_type) {
        message.error(lang(langIndex, "notification_status_type_required"));
        return;
      }
      if (ruleInfo.trigger_config.status_type === "downtime_reason") {
        if (!ruleInfo.trigger_config.downtime_reasons || ruleInfo.trigger_config.downtime_reasons.length === 0) {
          message.error(lang(langIndex, "notification_downtime_reasons_required"));
          return;
        }
      } else {
        // For other status types, time_threshold_minutes is required (0 allowed for immediate)
        const t = ruleInfo.trigger_config.time_threshold_minutes;
        if (t === undefined || t === null || t < 0) {
          message.error(lang(langIndex, "notification_time_threshold_required"));
          return;
        }
      }
    }

    // Validate notification methods
    if (!ruleInfo.notification_methods || ruleInfo.notification_methods.length === 0) {
      message.error(lang(langIndex, "notification_methods_required"));
      return;
    }

    // Convert text fields to arrays and validate recipients based on methods
    const emailArray = emailRecipientsText.split(';').map(email => email.trim()).filter(email => email);
    const phoneArray = phoneRecipientsText.split(';').map(phone => phone.trim()).filter(phone => phone);

    if (ruleInfo.notification_methods.includes("email")) {
      if (emailArray.length === 0) {
        message.error(lang(langIndex, "notification_email_recipients_required"));
        return;
      }
      // Validate email formats
      const invalidEmails = emailArray.filter(email => !isValidEmail(email));
      if (invalidEmails.length > 0) {
        message.error(`${lang(langIndex, "notification_invalid_email_format")}: ${invalidEmails.join(', ')}`);
        return;
      }
    }

    if (ruleInfo.notification_methods.includes("sms")) {
      if (phoneArray.length === 0) {
        message.error(lang(langIndex, "notification_phone_recipients_required"));
        return;
      }
      // Validate phone formats
      const invalidPhones = phoneArray.filter(phone => !isValidPhone(phone));
      if (invalidPhones.length > 0) {
        message.error(`${lang(langIndex, "notification_invalid_phone_format")}: ${invalidPhones.join(', ')}`);
        return;
      }
    }

    const triggerConfig = {
      ...ruleInfo.trigger_config,
      time_threshold_minutes:
        ruleInfo.trigger_config?.time_threshold_minutes !== undefined && ruleInfo.trigger_config?.time_threshold_minutes !== null
          ? ruleInfo.trigger_config.time_threshold_minutes
          : ruleInfo.trigger_type === "machine_status"
          ? 20
          : 30,
    };
    const submitData = {
      ...ruleInfo,
      trigger_config: triggerConfig,
      recipients: {
        ...ruleInfo.recipients,
        email: emailArray,
        phone: phoneArray,
      },
      id: initRuleInfo ? initRuleInfo.id : undefined,
      key: initRuleInfo ? initRuleInfo.key : undefined,
    };

    if (initRuleInfo == null) {
      addRule(submitData);
    } else {
      editRule(submitData);
    }
  };

  const onClickCancel = () => {
    setIsShowModal(false);
  };

  const getTriggerTypeOptions = () => {
    return [
      { value: "cycle_stop_alert", label: lang(langIndex, "notification_trigger_cycle_stop_alert") },
      { value: "machine_status", label: lang(langIndex, "notification_trigger_machine_status") },
      // Maintenance Task and IoT Sensor will be implemented later
      // { value: "maintenance_task", label: lang(langIndex, "notification_trigger_maintenance_task") },
      // { value: "iot_sensor", label: lang(langIndex, "notification_trigger_iot_sensor") },
    ];
  };

  const getStatusTypeOptions = () => {
    return [
      { value: "in_cycle", label: lang(langIndex, "notification_status_in_cycle") },
      { value: "not_in_cycle", label: lang(langIndex, "notification_status_not_in_cycle") },
      { value: "idle_uncategorized", label: lang(langIndex, "notification_status_idle_uncategorized") },
      { value: "offline", label: lang(langIndex, "notification_status_offline") },
      { value: "downtime_reason", label: lang(langIndex, "notification_status_downtime_reason") },
    ];
  };

  const onNotificationMethodChange = (checkedValues) => {
    setRuleInfo({ ...ruleInfo, notification_methods: checkedValues });
  };

  const onEmailRecipientsChange = (e) => {
    // Store raw text - allow semicolons to be typed
    setEmailRecipientsText(e.target.value);
  };

  const onPhoneRecipientsChange = (e) => {
    // Store raw text - allow semicolons to be typed
    setPhoneRecipientsText(e.target.value);
  };

  const onTriggerTypeChange = (value) => {
    // Reset trigger_config when changing trigger type
    let newConfig = {};
    if (value === "cycle_stop_alert") {
      newConfig = { machine_ids: [], time_threshold_minutes: 30 };
    } else if (value === "machine_status") {
      newConfig = { machine_ids: [], status_type: "offline", time_threshold_minutes: 20, downtime_reasons: [] };
    }
    setRuleInfo({ ...ruleInfo, trigger_type: value, trigger_config: newConfig });
  };

  const onMachineSelectionChange = (machineIds) => {
    const newConfig = {
      ...ruleInfo.trigger_config,
      machine_ids: machineIds,
    };
    // If machine_status with downtime_reason status_type, clear selected reasons when machines change
    if (ruleInfo.trigger_type === "machine_status" && ruleInfo.trigger_config?.status_type === "downtime_reason") {
      newConfig.downtime_reasons = [];
    }
    setRuleInfo({ ...ruleInfo, trigger_config: newConfig });
  };

  const onTimeThresholdChange = (value) => {
    const defaultMinutes = ruleInfo.trigger_type === "machine_status" ? 20 : 30;
    const minutes = value !== undefined && value !== null ? value : defaultMinutes;
    setRuleInfo({
      ...ruleInfo,
      trigger_config: {
        ...ruleInfo.trigger_config,
        time_threshold_minutes: minutes,
      },
    });
  };

  const onStatusTypeChange = (value) => {
    const newConfig = {
      ...ruleInfo.trigger_config,
      status_type: value,
      downtime_reasons: value === "downtime_reason" ? [] : (ruleInfo.trigger_config.downtime_reasons || []),
    };
    // If changing away from downtime_reason, clear downtime_reasons
    if (value !== "downtime_reason") {
      newConfig.downtime_reasons = [];
    }
    setRuleInfo({ ...ruleInfo, trigger_config: newConfig });
  };

  const onDowntimeReasonsChange = (reasons) => {
    setRuleInfo({
      ...ruleInfo,
      trigger_config: {
        ...ruleInfo.trigger_config,
        downtime_reasons: reasons,
      },
    });
  };

  return (
    <div>
      <Modal
        title={
          initRuleInfo == null
            ? lang(langIndex, "notification_rule_add_new")
            : lang(langIndex, "notification_rule_edit")
        }
        visible={isShowModal}
        onOk={onClickOk}
        onCancel={onClickCancel}
        destroyOnClose={true}
        className="user-setting-modal-style"
        width={700}
        getContainer={() => document.body}
        mask={true}
      >
        <div>
          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_rule_name")}</span>
            </Col>
            <Col span={16}>
              <Input
                style={{ background: "#1e1e1e", color: "#eeeeee" }}
                value={ruleInfo.rule_name || ""}
                onChange={(e) =>
                  setRuleInfo({ ...ruleInfo, rule_name: e.target.value })
                }
                placeholder="e.g., Machine Downtime Alert"
              />
            </Col>
          </Row>

          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_trigger_type")}</span>
            </Col>
            <Col span={16}>
              <Select
                value={ruleInfo.trigger_type}
                className="page-changer-style"
                dropdownClassName="page-changer-style-dropdown"
                onChange={onTriggerTypeChange}
                style={{ width: "100%" }}
              >
                {getTriggerTypeOptions().map((option) => (
                  <Option
                    key={option.value}
                    className="page-changer-item"
                    value={option.value}
                  >
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>

          {/* Trigger Configuration: Cycle Stop Alert */}
          {ruleInfo.trigger_type === "cycle_stop_alert" && (
            <>
              <Row style={{ marginTop: 10 }}>
                <Col span={8}>
                  <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_machines")}</span>
                </Col>
                <Col span={16}>
                  <Select
                    mode="multiple"
                    value={ruleInfo.trigger_config?.machine_ids || []}
                    className="page-changer-style"
                    dropdownClassName="page-changer-style-dropdown"
                    onChange={onMachineSelectionChange}
                    style={{ width: "100%" }}
                    placeholder={lang(langIndex, "notification_select_machines")}
                  >
                    {machineList.map((machine) => (
                      <Option
                        key={machine.machine_id}
                        className="page-changer-item"
                        value={machine.machine_id}
                      >
                        {machine.machine_id}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }}>
                <Col span={8}>
                  <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_downtime_minutes")}</span>
                </Col>
                <Col span={16}>
                  <InputNumber
                    style={{ background: "#1e1e1e", color: "#eeeeee", width: "100%" }}
                    value={ruleInfo.trigger_config?.time_threshold_minutes || 30}
                    onChange={onTimeThresholdChange}
                    min={1}
                    placeholder="30"
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Trigger Configuration: Machine Status */}
          {ruleInfo.trigger_type === "machine_status" && (
            <>
              <Row style={{ marginTop: 10 }}>
                <Col span={8}>
                  <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_machines")}</span>
                </Col>
                <Col span={16}>
                  <Select
                    mode="multiple"
                    value={ruleInfo.trigger_config?.machine_ids || []}
                    className="page-changer-style"
                    dropdownClassName="page-changer-style-dropdown"
                    onChange={onMachineSelectionChange}
                    style={{ width: "100%" }}
                    placeholder={lang(langIndex, "notification_select_machines")}
                  >
                    {machineList.map((machine) => (
                      <Option
                        key={machine.machine_id}
                        className="page-changer-item"
                        value={machine.machine_id}
                      >
                        {machine.machine_id}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }}>
                <Col span={8}>
                  <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_status_type")}</span>
                </Col>
                <Col span={16}>
                  <Select
                    value={ruleInfo.trigger_config?.status_type || "offline"}
                    className="page-changer-style"
                    dropdownClassName="page-changer-style-dropdown"
                    onChange={onStatusTypeChange}
                    style={{ width: "100%" }}
                  >
                    {getStatusTypeOptions().map((option) => (
                      <Option
                        key={option.value}
                        className="page-changer-item"
                        value={option.value}
                      >
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
              {ruleInfo.trigger_config?.status_type && 
               ruleInfo.trigger_config.status_type !== "downtime_reason" && (
                <Row style={{ marginTop: 10 }}>
                  <Col span={8}>
                    <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_time_threshold")}</span>
                  </Col>
                  <Col span={16}>
                    <InputNumber
                      style={{ background: "#1e1e1e", color: "#eeeeee", width: "100%" }}
                      value={ruleInfo.trigger_config?.time_threshold_minutes || 20}
                      onChange={onTimeThresholdChange}
                      min={0}
                      placeholder="20"
                    />
                  </Col>
                </Row>
              )}
              {ruleInfo.trigger_config?.status_type === "downtime_reason" && 
               ruleInfo.trigger_config?.machine_ids && 
               ruleInfo.trigger_config.machine_ids.length > 0 && (
                <Row style={{ marginTop: 10 }}>
                  <Col span={8}>
                    <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_downtime_reasons")}</span>
                  </Col>
                  <Col span={16}>
                    {downtimeReasons.length > 0 ? (
                      <Select
                        mode="multiple"
                        value={ruleInfo.trigger_config?.downtime_reasons || []}
                        className="page-changer-style"
                        dropdownClassName="page-changer-style-dropdown"
                        onChange={onDowntimeReasonsChange}
                        style={{ width: "100%" }}
                        placeholder={lang(langIndex, "notification_select_downtime_reasons")}
                      >
                        {downtimeReasons.map((reason) => (
                          <Option
                            key={reason}
                            className="page-changer-item"
                            value={reason}
                          >
                            {reason}
                          </Option>
                        ))}
                      </Select>
                    ) : (
                      <span style={{ color: "#999" }}>
                        {lang(langIndex, "notification_loading_downtime_reasons")}
                      </span>
                    )}
                  </Col>
                </Row>
              )}
              {ruleInfo.trigger_config?.status_type === "downtime_reason" && (
                <Row style={{ marginTop: 10 }}>
                  <Col span={8}>
                    <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_time_threshold")}</span>
                  </Col>
                  <Col span={16}>
                    <InputNumber
                      style={{ background: "#1e1e1e", color: "#eeeeee", width: "100%" }}
                      value={ruleInfo.trigger_config?.time_threshold_minutes || 0}
                      onChange={onTimeThresholdChange}
                      min={0}
                      placeholder="0 (immediate)"
                    />
                  </Col>
                </Row>
              )}
            </>
          )}

          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_methods")}</span>
            </Col>
            <Col span={16}>
              <Checkbox.Group
                value={ruleInfo.notification_methods}
                onChange={onNotificationMethodChange}
                className="notification-checkbox-group"
              >
                <Checkbox value="email" className="notification-checkbox">
                  {lang(langIndex, "notification_method_email")}
                </Checkbox>
                <Checkbox value="sms" className="notification-checkbox">
                  {lang(langIndex, "notification_method_sms")}
                </Checkbox>
                <Checkbox value="app" className="notification-checkbox">
                  {lang(langIndex, "notification_method_app")}
                </Checkbox>
              </Checkbox.Group>
            </Col>
          </Row>

          {ruleInfo.notification_methods.includes("email") && (
            <Row style={{ marginTop: 10 }}>
              <Col span={8}>
                <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_recipients_email")}</span>
              </Col>
              <Col span={16}>
                <TextArea
                  style={{ background: "#1e1e1e", color: "#eeeeee" }}
                  value={emailRecipientsText}
                  onChange={onEmailRecipientsChange}
                  placeholder="email1@example.com; email2@example.com"
                  rows={3}
                />
              </Col>
            </Row>
          )}

          {ruleInfo.notification_methods.includes("sms") && (
            <Row style={{ marginTop: 10 }}>
              <Col span={8}>
                <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_recipients_phone")}</span>
              </Col>
              <Col span={16}>
                <TextArea
                  style={{ background: "#1e1e1e", color: "#eeeeee" }}
                  value={phoneRecipientsText}
                  onChange={onPhoneRecipientsChange}
                  placeholder="+1234567890; +0987654321"
                  rows={3}
                />
              </Col>
            </Row>
          )}

          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_timing")}</span>
            </Col>
            <Col span={16}>
              <Select
                value={ruleInfo.timing_type}
                className="page-changer-style"
                dropdownClassName="page-changer-style-dropdown"
                onChange={(value) =>
                  setRuleInfo({ ...ruleInfo, timing_type: value })
                }
                style={{ width: "100%" }}
              >
                <Option value="immediate">{lang(langIndex, "notification_timing_immediate")}</Option>
                <Option value="delayed">{lang(langIndex, "notification_timing_delayed")}</Option>
              </Select>
            </Col>
          </Row>

          {ruleInfo.timing_type === "delayed" && (
            <Row style={{ marginTop: 10 }}>
              <Col span={8}>
                <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_delay_minutes")}</span>
              </Col>
              <Col span={16}>
                <InputNumber
                  style={{ background: "#1e1e1e", color: "#eeeeee", width: "100%" }}
                  value={ruleInfo.delay_minutes}
                  onChange={(value) =>
                    setRuleInfo({ ...ruleInfo, delay_minutes: value || 0 })
                  }
                  min={1}
                  placeholder="Minutes"
                />
              </Col>
            </Row>
          )}

          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_repeat")}</span>
            </Col>
            <Col span={16}>
              <Select
                value={ruleInfo.repeat_type}
                className="page-changer-style"
                dropdownClassName="page-changer-style-dropdown"
                onChange={(value) =>
                  setRuleInfo({ ...ruleInfo, repeat_type: value })
                }
                style={{ width: "100%" }}
              >
                <Option value="one_time">{lang(langIndex, "notification_repeat_one_time")}</Option>
                <Option value="repeat">{lang(langIndex, "notification_repeat_repeat")}</Option>
              </Select>
            </Col>
          </Row>

          {ruleInfo.repeat_type === "repeat" && (
            <Row style={{ marginTop: 10 }}>
              <Col span={8}>
                <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_repeat_interval")}</span>
              </Col>
              <Col span={16}>
                <InputNumber
                  style={{ background: "#1e1e1e", color: "#eeeeee", width: "100%" }}
                  value={ruleInfo.repeat_interval_minutes}
                  onChange={(value) =>
                    setRuleInfo({ ...ruleInfo, repeat_interval_minutes: value || null })
                  }
                  min={1}
                  placeholder="Minutes"
                />
              </Col>
            </Row>
          )}

          <Row style={{ marginTop: 10 }}>
            <Col span={8}>
              <span style={{ color: "#eeeeee" }}>{lang(langIndex, "notification_active")}</span>
            </Col>
            <Col span={16}>
              <Switch
                className="notification-active-switch"
                size="default"
                checked={ruleInfo.is_active === 1 || ruleInfo.is_active === "1" || ruleInfo.is_active === true}
                onChange={(checked) =>
                  setRuleInfo({ ...ruleInfo, is_active: checked ? 1 : 0 })
                }
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
}

export default NotificationRuleDialog;

